import { Divider, Grid } from '@mui/material';
import { HexColor, Location } from '@nx-smartmonkey/shared/domain';
import { TimeWindow } from '@nx-smartmonkey/shared/interfaces';
import { HSpacer, VSpacer } from '@nx-smartmonkey/ui';
import { useEffect } from 'react';

import { NewVehiclePill } from '../../../../components/NewVehiclePill';
import { Organization } from '../../../../domain/organization/Organization';
import { Project } from '../../../../domain/project/Project';
import { Vehicle } from '../../../../domain/vehicle/Vehicle';
import Button from '../../../../react-components/Button';
import Text from '../../../../react-components/Text';

export interface CreateVehiclesConstraints {
  volume: boolean;
  weight: boolean;
}

interface CreateVehiclesProps {
  project: Project;
  organization: Organization;
  vehiclesTimeWindow: TimeWindow;
  vehiclesLocation: {
    start?: Location;
    end?: Location;
  };
  vehicles: Vehicle[];
  constraints: CreateVehiclesConstraints;
  onChange: (vehicles: Vehicle[]) => void;
}

export const CreateVehicles = ({
  project,
  organization,
  vehiclesTimeWindow,
  vehiclesLocation,
  vehicles,
  constraints,
  onChange,
}: CreateVehiclesProps) => {
  useEffect(() => {
    if (vehicles.length === 0) {
      onChange([createVehicle()]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [vehicles]);

  const createVehicle = () =>
    Vehicle.create(
      {
        organization_id: organization.id,
        project_id: project.id,
        label: ``,
        default_start_location: vehiclesLocation.start,
        default_end_location: vehiclesLocation.end,
        default_time_window: vehiclesTimeWindow,
        color: HexColor.create(Vehicle.getDefaultColor(vehicles)),
        ...(constraints.volume && { default_max_volume: 0 }),
        ...(constraints.weight && { default_max_weight: 0 }),
        enabled: true,
      },
      `id-${Math.random()}`
    );

  return (
    <Grid container direction="column" alignItems="flex-start" wrap="nowrap" style={{ height: `100%` }}>
      <Grid
        id={`content-animated`}
        className={`animate__animated animate__fadeIn`}
        container
        direction="column"
        wrap="nowrap"
        style={{ paddingTop: `20px`, paddingBottom: `20px`, height: `100%` }}
      >
        <Grid
          item
          container
          padding={`4px 20px 24px`}
          style={{ display: `flex`, flexWrap: `nowrap`, justifyContent: `space-between` }}
        >
          <Grid item container direction="column" wrap="nowrap" alignItems="flex-start">
            <Grid item>
              <Text variant="h6" intlId="company.onboarding.vehicles.create.info" />
            </Grid>
            <VSpacer medium />
            <Grid item style={{ maxWidth: `750px` }}>
              <Text
                variant="label"
                style={{ fontSize: `14px` }}
                intlId="company.onboarding.vehicles.create.description"
              />
            </Grid>
          </Grid>
          <HSpacer medium />
          <Grid item style={{ display: `flex`, alignItems: `center` }}>
            <Button
              disabled={vehicles.length >= 5}
              styleType="primary"
              variant="flat"
              onClick={() => {
                onChange([...vehicles, createVehicle()]);
              }}
              style={{ height: `48px`, padding: `0 28px`, minWidth: `106px` }}
              intlId="company.onboarding.vehicles.create.add"
            />
          </Grid>
        </Grid>
        <Divider />

        <Grid
          container
          style={{
            height: `calc(100% - 185px)`,
            overflowY: `auto`,
            display: `flex`,
            justifyContent: `center`,
            flexWrap: `nowrap`,
            flexDirection: `column`,
          }}
        >
          <Grid item container direction="column" wrap="nowrap" alignItems="center">
            {vehicles.map((vehicle, index) => (
              <Grid item key={vehicle.id} style={{ width: `100%`, maxWidth: `650px` }}>
                <NewVehiclePill
                  project={project}
                  organization={organization}
                  vehicle={vehicle}
                  constraints={constraints}
                  onUpdate={(updatedVehicle: Vehicle) => {
                    const updatedVehicles = vehicles.map((v) => {
                      if (v.id === updatedVehicle.id) {
                        return updatedVehicle;
                      }
                      return v;
                    });
                    onChange(updatedVehicles);
                  }}
                  canRemove={vehicles.length > 1}
                  onRemove={(vehicleToRemove: Vehicle) => {
                    const updatedVehicles = vehicles.filter((v) => v.id !== vehicleToRemove.id);
                    onChange(updatedVehicles);
                  }}
                />

                {index < vehicles.length - 1 && <VSpacer medium />}
              </Grid>
            ))}
          </Grid>
        </Grid>
      </Grid>
    </Grid>
  );
};
